import React, { useEffect, useMemo, useState } from "react";
import FarmerLayout from "../../components/layouts/FarmerLayout";
import AttachCompanyModal from "./components/AttachCompanyModal";
import toast from "react-hot-toast";
import { auth, firestore, database } from "../../firebaseConfig";
import { onAuthStateChanged } from "firebase/auth";
import { collection, doc, getDoc, getDocs, updateDoc, writeBatch } from "firebase/firestore";
import { ref, remove, set } from "firebase/database";
import { buildThreadId } from "../../components/messaging/helpers";

/**
 * FINAL UPDATED LOGIC (2025)
 *
 * Firestore user structure:
 *  - companyName: "" | string
 *  - pendingAttach: {
 *        companyName,
 *        requestedAt,
 *        status: "pending" | "approved" | "declined",
 *        decidedAt?
 *    }
 *
 * Realtime DB structure:
 *  - FarmerCompanyRequests/{uid} → farmer sends request
 *  - Companies/{companyName}/ValidatedFarmers/{key}/LinkedFarmer → company accepts
 *
 * Farmer UI states:
 *  1. No company, no request → show "Link with Company"
 *  2. Pending → show cancel
 *  3. Approved → show linked company + detach
 *  4. Declined → show message + allow sending new request
 */

const FarmerSettings = () => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [companyInfo, setCompanyInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAttachModal, setShowAttachModal] = useState(false);
  const [showDetachModal, setShowDetachModal] = useState(false);
  const [detachConfirmation, setDetachConfirmation] = useState("");
  const [processingDetach, setProcessingDetach] = useState(false);

  // AUTH WATCH
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      setUser(u || null);
    });
    return () => unsub();
  }, []);

  // LOAD USER PROFILE + COMPANY DATA
  useEffect(() => {
    let mounted = true;

    const load = async () => {
      setLoading(true);

      try {
        if (!user) {
          setProfile(null);
          setCompanyInfo(null);
          setLoading(false);
          return;
        }

        const userRef = doc(firestore, "users", user.uid);
        const snap = await getDoc(userRef);
        const data = snap.exists() ? snap.data() : null;

        if (!mounted) return;

        setProfile(data || null);

        // Load company if user is LINKED
        if (data?.companyName && data.pendingAttach?.status !== "declined") {
          const companyRef = doc(
            firestore,
            "companies",
            data.companyName.replace(/\//g, "-")
          );
          const cSnap = await getDoc(companyRef);
          setCompanyInfo(cSnap.exists() ? cSnap.data() : null);
        } else {
          setCompanyInfo(null);
        }
      } catch (err) {
        console.error(err);
        toast.error("Failed to load profile");
      } finally {
        if (mounted) setLoading(false);
      }
    };

    load();
    return () => {
      mounted = false;
    };
  }, [user]);

  const userIdentifier = useMemo(() => {
    return (
      profile?.username ||
      profile?.otherName ||
      profile?.fullName ||
      profile?.email ||
      user?.email ||
      user?.uid ||
      "your account"
    );
  }, [profile, user]);

  const messagingParticipantId = useMemo(() => {
    return (
      profile?.otherName ||
      profile?.username ||
      profile?.fullName ||
      user?.uid ||
      ""
    );
  }, [profile, user]);

  const clearMessagingThread = async (companyName) => {
    const participantId = messagingParticipantId;
    const threadId = buildThreadId(companyName, participantId);
    if (!threadId) return;
    try {
      const batch = writeBatch(firestore);
      const threadRef = doc(firestore, "companyMessages", threadId);
      batch.set(
        threadRef,
        {
          archivedForCompany: true,
          archivedForParticipant: true,
          lastClearedBy: "participant",
        },
        { merge: true }
      );
      const snap = await getDocs(collection(firestore, "companyMessages", threadId, "messages"));
      snap.forEach((m) => {
        const msgRef = doc(firestore, "companyMessages", threadId, "messages", m.id);
        batch.update(msgRef, {
          deletedForCompany: true,
          deletedForParticipant: true,
        });
      });
      await batch.commit();
    } catch (err) {
      console.error("Failed to clear messaging thread during detach", err);
    }
  };

  // ===========================================
  // DETACH REQUEST
  // ===========================================
  const handleDetach = async () => {
    if (!user || !profile?.companyName) return false;

    const companyName = profile.companyName;

    try {
      const uid = user.uid;

      await clearMessagingThread(companyName);

      // Remove link on Firestore
      await updateDoc(doc(firestore, "users", uid), {
        companyName: "",
        pendingAttach: null,
      });

      // Notify company admins
      const detachRef = ref(
        database,
        `Companies/${companyName}/DetachRequests/${uid}`
      );
      await set(detachRef, {
        uid,
        fullName: profile.fullName || "",
        requestedAt: new Date().toISOString(),
        status: "requested",
      });

      toast.success("Detach request sent");

      // Update UI state
      setProfile((p) => ({
        ...(p || {}),
        companyName: "",
        pendingAttach: null,
      }));
      setCompanyInfo(null);
      return true;
    } catch (err) {
      console.error(err);
      toast.error("Failed to detach");
      return false;
    }
  };

  const confirmDetach = async () => {
    if (!profile?.companyName) return;
    const normalizedInput = detachConfirmation.trim().toLowerCase();
    const expectedValues = [profile.companyName, userIdentifier]
      .filter(Boolean)
      .map((v) => v.toLowerCase());
    const isValid = expectedValues.some((v) => v === normalizedInput);

    if (!isValid) {
      toast.error("Type the company name or your username to detach.");
      return;
    }

    setProcessingDetach(true);
    const success = await handleDetach();
    setProcessingDetach(false);

    if (success) {
      setShowDetachModal(false);
      setDetachConfirmation("");
    }
  };

  // ===========================================
  // CANCEL ATTACH REQUEST
  // ===========================================
  const cancelPendingRequest = async () => {
    if (!user || !profile?.pendingAttach?.companyName) return;

    const uid = user.uid;
    const comp = profile.pendingAttach.companyName;

    try {
      // Remove realtime request
      await remove(ref(database, `FarmerCompanyRequests/${uid}`));

      // Remove Firestore pendingAttach
      await updateDoc(doc(firestore, "users", uid), {
        pendingAttach: null,
      });

      toast.success(`Request to ${comp} cancelled.`);

      setProfile((p) => ({
        ...(p || {}),
        pendingAttach: null,
      }));
    } catch (err) {
      console.error(err);
      toast.error("Could not cancel request");
    }
  };

  // =================================================================
  // UI RENDERING BASED ON STATE
  // =================================================================

  if (loading) {
    return (
      <FarmerLayout>
        <div className="p-6">Loading profile...</div>
      </FarmerLayout>
    );
  }

  const pending = profile?.pendingAttach;
  const isLinked = profile?.companyName && (!pending || pending.status === "approved");

  return (
    <FarmerLayout>
      <div className="p-6 max-w-4xl mx-auto">

        {/* HEADER */}
        <div className="bg-white p-6 rounded-xl shadow flex gap-6">
          <div className="w-28 h-28 rounded-full bg-green-100 flex items-center justify-center text-green-700 text-2xl font-bold">
            {profile?.fullName
              ? profile.fullName.split(" ").map((s) => s[0]).slice(0, 2).join("")
              : "F"}
          </div>

          <div className="flex-1">
            <h2 className="text-2xl font-semibold text-green-700">
              {profile?.fullName || "Farmer"}
            </h2>
            <p className="text-gray-500 text-sm">{profile?.email}</p>

            {/* STATUS MESSAGE */}
            <p className="mt-2 text-sm text-gray-700">
              {isLinked
                ? `Linked to ${profile.companyName}`
                : pending?.status === "pending"
                ? `Pending approval from ${pending.companyName}`
                : pending?.status === "declined"
                ? `Your request to ${pending.companyName} was declined`
                : "Not linked to any company"}
            </p>

            {/* ACTION BUTTONS */}
            <div className="mt-3 flex gap-2">
              {isLinked ? (
                <>
                  <button
                    onClick={() => setShowDetachModal(true)}
                    className="px-4 py-2 bg-amber-500 text-white rounded hover:bg-amber-600"
                  >
                    Detach from {profile.companyName}
                  </button>
                </>
              ) : pending?.status === "pending" ? (
                <>
                  <button
                    onClick={cancelPendingRequest}
                    className="px-4 py-2 border rounded"
                  >
                    Cancel Request
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={() => setShowAttachModal(true)}
                    className="px-4 py-2 bg-cyan-600 text-white rounded hover:bg-cyan-700"
                  >
                    Link with Company
                  </button>
                </>
              )}
            </div>
          </div>

          {/* COMPANY CARD WHEN LINKED */}
          {isLinked && companyInfo && (
            <div className="w-56 p-3 bg-cyan-50 border rounded">
              <h4 className="text-cyan-700 font-semibold">
                {companyInfo.companyName}
              </h4>
              <p className="text-xs text-gray-500">
                Reg: {companyInfo.registrationNumber}
              </p>
            </div>
          )}
        </div>

        {/* ADDITIONAL SETTINGS */}
        <div className="mt-6 bg-white p-6 rounded shadow-sm">
          <h3 className="font-semibold text-gray-800 mb-2">Profile Options</h3>
          <p className="text-sm text-gray-600">
            More profile controls will appear here.
          </p>
        </div>
      </div>

      {/* ATTACH MODAL */}
      {showAttachModal && (
        <AttachCompanyModal
          open={showAttachModal}
          onClose={() => setShowAttachModal(false)}
          user={user}
          onRequestSent={async () => {
            // Refresh Firestore
            const snap = await getDoc(doc(firestore, "users", user.uid));
            setProfile(snap.data());
            setShowAttachModal(false);
          }}
        />
      )}

      {showDetachModal && (
        <ConfirmDetachModal
          companyName={profile?.companyName}
          userIdentifier={userIdentifier}
          confirmationValue={detachConfirmation}
          onChangeConfirmation={setDetachConfirmation}
          onClose={() => {
            setShowDetachModal(false);
            setDetachConfirmation("");
          }}
          onConfirm={confirmDetach}
          loading={processingDetach}
        />
      )}
    </FarmerLayout>
  );
};

export default FarmerSettings;

const ConfirmDetachModal = ({
  companyName,
  userIdentifier,
  confirmationValue,
  onChangeConfirmation,
  onClose,
  onConfirm,
  loading,
}) => {
  const expectedLabel = [companyName, userIdentifier].filter(Boolean).join(" / ");
  const normalizedInput = confirmationValue.trim().toLowerCase();
  const expectedValues = [companyName, userIdentifier]
    .filter(Boolean)
    .map((v) => v.toLowerCase());
  const isMatch = expectedValues.some((v) => v === normalizedInput);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 px-4">
      <div className="bg-white rounded-xl shadow-lg max-w-md w-full p-6 space-y-4">
        <div>
          <h3 className="text-xl font-semibold text-gray-800">We&apos;re sad to see you go</h3>
          <p className="text-sm text-gray-600 mt-1">
            Detaching from <span className="font-semibold text-gray-800">{companyName}</span> will end your connection.
            Please type <span className="font-semibold text-gray-800">{expectedLabel}</span> (no pasting) to confirm. Thank you for being with us.
          </p>
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700">
            Type exactly: <span className="text-gray-900">{expectedLabel}</span>
          </label>
          <input
            type="text"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500"
            value={confirmationValue}
            onChange={(e) => onChangeConfirmation(e.target.value)}
            onPaste={(e) => e.preventDefault()}
            placeholder="Enter the value to confirm"
          />
          <p className="text-xs text-gray-500">
            Copy/paste is disabled to be sure this is really you.
          </p>
        </div>

        <div className="flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
            disabled={loading}
          >
            Stay Attached
          </button>
          <button
            onClick={onConfirm}
            disabled={!isMatch || loading}
            className={`px-4 py-2 rounded-lg text-white transition ${
              isMatch && !loading
                ? "bg-amber-600 hover:bg-amber-700"
                : "bg-amber-300 cursor-not-allowed"
            }`}
          >
            {loading ? "Detaching..." : "Detach"}
          </button>
        </div>
      </div>
    </div>
  );
};
